<template>
  <div>
    <div v-if="state === 'initial'">
      <div class="row pb-3">
        <div class="col-sm-12 col-md-6">
          {{ $t('BoardsAssemblyGuide.side') }}
        </div>
        <div class="col-12 col-md-6">
          <select v-model="side" class="form-select">
            <option value="top">
              {{ $t('BoardsAssemblyGuide.top') }}
            </option>
            <option value="bottom">
              {{ $t('BoardsAssemblyGuide.bottom') }}
            </option>
          </select>
        </div>
      </div>

      <div class="row mb-3">
        <div class="col-12">
          {{ $t('BoardsAssemblyGuide.intro') }}
        </div>
      </div>

      <div class="row mb-3">
        <div class="col-12 text-center">
          <button
            type="button"
            @click="start"
            class="btn btn-primary btn-lg"
            v-if="currentPart"
          >
            {{ $t('BoardsAssemblyGuide.start') }}
          </button>
          <b v-else>
            {{ $t(`BoardsAssemblyGuide.${side}`) }} {{ $t('BoardsAssemblyGuide.nothing_to_place') }}
          </b>
        </div>
      </div>
    </div>

    <div v-if="state === 'assembly'">
      <div class="row text-center">
        <div class="col-12 text-center">
          {{ $t('BoardsAssemblyGuide.part') }} {{ position + 1 }} {{ $t('BoardsAssemblyGuide.of') }} {{ total }}
        </div>
        <div class="col-12">
          <div
            class="progress"
            role="progressbar"
            :aria-valuenow="progress"
            aria-valuemax="100"
            aria-valuemin="0"
          >
            <div class="progress-bar" :style="{ width: `${ progress }%` }" />
          </div>
        </div>
      </div>

      <hr>

      <div class="row g-3">
        <div class="col-12 col-md-2">
          <button type="button" @click="previousChunk" class="btn btn-sm btn-secondary">
            <i class="fas fa-chevron-left" />
          </button>
        </div>

        <div class="col-12 col-md-8">
          <b>{{ currentPart.description }}</b>
          <span v-if="currentPart.mpn">({{ currentPart.mpn }})</span>
        </div>

        <div class="col-12 col-md-2 text-end">
          <button type="button" @click="confirmChunk" class="btn btn-sm btn-primary">
            <i class="fas fa-check" />
          </button>
        </div>

        <div class="col-12">
          <ul class="list-group">
            <li
              v-for="part in currentPart.placements[side][chunk]"
              :key="part.id"
              class="list-group-item"
            >
              {{ part.name }}
            </li>
          </ul>
        </div>

        <div class="col-12 text-center" v-if="chunksAvailable">
          {{ $t('BoardsAssemblyGuide.chunk') }}
          {{ chunk + 1 }}
          {{ $t('BoardsAssemblyGuide.of') }}
          {{ currentPart.placements[side].length }}
        </div>
      </div>
    </div>

    <div v-if="state === 'done'">
      <div class="row text-center mb-3">
        <div class="col-12">
          <b>{{ $t('BoardsAssemblyGuide.done') }}</b>
        </div>
      </div>

      <div class="row text-center">
        <div class="col-12">
          <button type="button" @click="reset" class="btn btn-primary btn-large">
            {{ $t('BoardsAssemblyGuide.reset') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import messages from 'locales/BoardsAssemblyGuide';

export default {
  name: 'SmartGuide',
  props: {
    parts: Array,
    projectToken: String,
  },
  data() {
    return {
      bus: this.$root.bus,
      position: 0,
      side: 'top',
      chunk: 0,
      state: 'initial',
    };
  },
  computed: {
    currentPart() {
      return this.partsBySide[this.position];
    },
    total() {
      return this.partsBySide.length;
    },
    progress() {
      return (100.0 / this.partsBySide.length) * (this.position + 1);
    },
    partsBySide() {
      return this.parts.filter((part) => part.placements[this.side].length > 0);
    },
    chunksAvailable() {
      return this.currentPart.placements[this.side].length > 1;
    },
  },
  mounted() {
  },
  methods: {
    updateViewer() {
      const self = this;
      let placed = [];
      this.partsBySide.forEach((part, ixPart) => {
        if (self.position === ixPart) {
          part.placements[this.side].forEach((chunk, ixChunk) => {
            if (self.chunk === ixChunk) {
              self.bus.emit('updatePartFocus', { parts: chunk, updateCamera: true });
            }
          });
        } else if (self.position > ixPart) {
          part.placements[this.side].forEach((chunk) => {
            placed = placed.concat(chunk);
          });
        }
      });
      self.bus.emit('updatePartPlacement', placed);
    },
    confirmChunk() {
      if ((this.currentPart.placements[this.side].length - 1) === this.chunk) {
        if (this.partsBySide.length === this.position + 1) {
          this.state = 'done';
          this.bus.emit('updatePartFocus', { parts: [], updateCamera: false });
          this.bus.emit('changePerspective', this.side === 'top' ? 'front' : 'back');
          this.position = 0;
          this.chunk = 0;
        } else {
          this.chunk = 0;
          this.position += 1;
          this.updateViewer();
        }
      } else {
        this.chunk += 1;
        this.updateViewer();
      }
    },
    previousChunk() {
      if (this.chunk >= 1) {
        this.chunk -= 1;
      } else if (this.position >= 1) {
        this.position -= 1;
      }
      this.updateViewer();
    },
    handleKeyEvents(evt) {
      if (this.state === 'done') return;

      switch (evt.key) {
        case ' ':
        case 'n':
          this.confirmChunk();
          break;
        case 'b':
          this.previousChunk();
          break;
        default:
          break;
      }
    },
    start() {
      this.state = 'assembly';
      window.addEventListener('keypress', this.handleKeyEvents);
      this.updateViewer();
    },
    reset() {
      this.state = 'initial';
      window.removeEventListener('keypress', this.handleKeyEvents);
      this.bus.emit('updatePartPlacement', []);
    },
    localStorageKey(key) {
      return ['project', this.projectToken, 'guide', key].join('_');
    },
  },
  i18n: { messages },
};
</script>

<style scoped lang="scss">
</style>
